import React, { Component } from "react";
import mapboxgl from "mapbox-gl";
import { toLngLat } from "../../utils/coordinates";
import { getEntryCoordinates } from "../../utils/haltMapData";

mapboxgl.accessToken = `pk.eyJ1IjoiYXl1c2hpbnR1Z2luZSIsImEiOiJjbHMxaWdramYwZzV3Mm1tazU4aTA5MzlkIn0.2EpIwfNReCEYLuSmPRT3Ag`;

const VEHICLE_COLORS = [
  "#FE5858",
  "#28AE14",
  "#4A90E2",
  "#F5A623",
  "#9013FE",
  "#50E3C2",
  "#BD10E0",
  "#F8E71C",
  "#7ED321",
  "#417505",
  "#4A4A4A",
  "#FF6B6B",
  "#1ABC9C",
  "#E67E22",
  "#3498DB",
  "#9B59B6",
];

const HALT_SOURCE_ID = "halt-points";
const HALT_LAYER_ID = "halt-points-layer";

const buildVehicleColorMap = (entries = []) => {
  const colorMap = {};
  let colorIndex = 0;

  entries.forEach((entry) => {
    const vehicle = entry.vehicle || "Unknown";
    if (!colorMap[vehicle]) {
      colorMap[vehicle] = VEHICLE_COLORS[colorIndex % VEHICLE_COLORS.length];
      colorIndex += 1;
    }
  });

  return colorMap;
};

const formatHaltLabel = (vehicle, lngLat) => {
  const lat = Number(lngLat[1]).toFixed(5);
  const lng = Number(lngLat[0]).toFixed(5);
  return `Vehicle :${vehicle || ""}, </br>Location : ${lat}, ${lng}`;
};

export class Haltmap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isLoaded: false,
      vehicleLegend: [],
    };

    this.markers = [];
    this.haltPopup = null;
    this.onHaltMouseEnter = null;
    this.onHaltMouseLeave = null;
  }

  clearMarkers() {
    this.markers.forEach((marker) => marker.remove());
    this.markers = [];
  }

  clearHaltLayer() {
    if (!this.map) return;

    if (this.onHaltMouseEnter) {
      this.map.off("mouseenter", HALT_LAYER_ID, this.onHaltMouseEnter);
      this.onHaltMouseEnter = null;
    }

    if (this.onHaltMouseLeave) {
      this.map.off("mouseleave", HALT_LAYER_ID, this.onHaltMouseLeave);
      this.onHaltMouseLeave = null;
    }

    if (this.haltPopup) {
      this.haltPopup.remove();
      this.haltPopup = null;
    }

    if (this.map.getLayer(HALT_LAYER_ID)) {
      this.map.removeLayer(HALT_LAYER_ID);
    }

    if (this.map.getSource(HALT_SOURCE_ID)) {
      this.map.removeSource(HALT_SOURCE_ID);
    }
  }

  componentDidMount() {
    this.map = new mapboxgl.Map({
      container: this.mapContainer,
      style: "mapbox://styles/mapbox/dark-v10",
      center: [78.9629, 20.5937],
      zoom: 4,
    });

    this.map.addControl(new mapboxgl.FullscreenControl());
    this.map.addControl(new mapboxgl.NavigationControl());
    this.map.on("load", () => {
      this.setState({ isLoaded: true });
      this.updateMarkers();
      this.map.resize();
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const dataChanged =
      prevProps.data !== this.props.data ||
      prevProps.src !== this.props.src ||
      prevProps.dest !== this.props.dest;
    const mapReady = this.state.isLoaded && !prevState.isLoaded;

    if ((dataChanged || mapReady) && this.map && this.state.isLoaded) {
      this.updateMarkers();
    }
  }

  addMarker(lngLat, className) {
    const el = document.createElement("div");
    el.className = className;
    const marker = new mapboxgl.Marker({
      element: el,
      anchor: "center",
    })
      .setLngLat(lngLat)
      .addTo(this.map);

    this.markers.push(marker);
    return lngLat;
  }

  updateHaltLayer(features) {
    this.clearHaltLayer();

    if (!features.length) return;

    this.map.addSource(HALT_SOURCE_ID, {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features,
      },
    });

    this.map.addLayer({
      id: HALT_LAYER_ID,
      type: "circle",
      source: HALT_SOURCE_ID,
      paint: {
        "circle-color": ["get", "color"],
        "circle-radius": 9,
        "circle-stroke-width": 2,
        "circle-stroke-color": "#ffffff",
      },
    });

    this.haltPopup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
    });

    this.onHaltMouseEnter = (event) => {
      this.map.getCanvas().style.cursor = "pointer";
      const feature = event.features && event.features[0];
      if (!feature) return;

      this.haltPopup
        .setLngLat(feature.geometry.coordinates)
        .setHTML(feature.properties.label)
        .addTo(this.map);
    };

    this.onHaltMouseLeave = () => {
      this.map.getCanvas().style.cursor = "";
      this.haltPopup.remove();
    };

    this.map.on("mouseenter", HALT_LAYER_ID, this.onHaltMouseEnter);
    this.map.on("mouseleave", HALT_LAYER_ID, this.onHaltMouseLeave);
  }

  updateMarkers() {
    if (!this.map) return;

    this.clearMarkers();
    this.clearHaltLayer();

    const data = Array.isArray(this.props.data) ? this.props.data : [];
    const plottedCoords = [];
    const vehicleColorMap = buildVehicleColorMap(data);
    const haltFeatures = [];

    data.forEach((entry) => {
      const coordinates = getEntryCoordinates(entry);
      if (!coordinates.length) return;

      const vehicle = entry.vehicle || "Unknown";
      const color = vehicleColorMap[vehicle] || VEHICLE_COLORS[0];

      coordinates.forEach((lngLat) => {
        haltFeatures.push({
          type: "Feature",
          properties: {
            vehicle,
            color,
            label: formatHaltLabel(vehicle, lngLat),
          },
          geometry: {
            type: "Point",
            coordinates: lngLat,
          },
        });
        plottedCoords.push(lngLat);
      });
    });

    this.updateHaltLayer(haltFeatures);

    const source = toLngLat(this.props.src);
    if (source) {
      plottedCoords.push(this.addMarker(source, "source_marker"));
    }

    const destination = toLngLat(this.props.dest);
    if (destination) {
      plottedCoords.push(this.addMarker(destination, "destination_marker"));
    }

    const vehicleLegend = Object.keys(vehicleColorMap).map((vehicle) => ({
      vehicle,
      color: vehicleColorMap[vehicle],
    }));

    this.setState({ vehicleLegend });

    if (plottedCoords.length) {
      const bounds = plottedCoords.reduce(
        (acc, coord) => acc.extend(coord),
        new mapboxgl.LngLatBounds(plottedCoords[0], plottedCoords[0])
      );

      this.map.fitBounds(bounds, {
        padding: 60,
        maxZoom: 10,
      });
    }
  }

  componentWillUnmount() {
    this.clearMarkers();
    this.clearHaltLayer();
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  render() {
    return (
      <div className="halt-map-wrapper">
        <div
          className="halt-map-container"
          ref={(el) => {
            this.mapContainer = el;
          }}
        />
        {this.state.vehicleLegend.length > 0 && (
          <div className="halt-vehicle-legend">
            {this.state.vehicleLegend.map((item) => (
              <div key={item.vehicle} className="halt-vehicle-legend-item">
                <span
                  className="halt-vehicle-legend-swatch"
                  style={{ backgroundColor: item.color }}
                />
                <span>{item.vehicle}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default Haltmap;
